import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { FunctionComponent, useMemo, useState } from 'react';

import { HeaderCard } from '../../../event/components/HeaderCard';
import { useCurrentEventQuery } from '../../../event/hooks/useCurrentEventQuery';
import { Layout } from '../../../navigation/components/Layout/Layout';
import { useCurrentUserDjQuery } from '../../hooks/useCurrentUserDjQuery';
import { useRequestDjMutation } from '../../hooks/useRequestDjMutation';

export const DjRequest: FunctionComponent = () => {
  const { data: currentEventData } = useCurrentEventQuery();
  const eventId = currentEventData?.currentEvent?.id ?? null;
  const hasPaidTicket = currentEventData?.currentEvent?.hasPaidTicket ?? false;

  const { data: currentUserDjData } = useCurrentUserDjQuery(eventId);
  const hasRequestedDj =
    currentUserDjData?.currentUser?.hasRequestedDj ?? false;

  const [djStyle, setDjStyle] = useState('');
  const [djTime, setDjTime] = useState('');
  const [hasAcceptedRules, setHasAcceptedRules] = useState(false);

  const { requestDj, loading } = useRequestDjMutation(() => {
    setDjStyle('');
    setDjTime('');
  });

  const isSubmitDisabled = useMemo(() => {
    return (
      !eventId ||
      loading ||
      djStyle.trim().length === 0 ||
      djTime.trim().length === 0 ||
      !hasAcceptedRules
    );
  }, [djStyle, djTime, eventId, hasAcceptedRules, loading]);

  const onSubmit = () => {
    if (
      !eventId ||
      djStyle.trim().length === 0 ||
      djTime.trim().length === 0 ||
      !hasAcceptedRules
    ) {
      return;
    }

    requestDj({
      eventId,
      djStyle: djStyle.trim(),
      djTime: djTime.trim(),
    });
  };

  if (!hasPaidTicket) {
    return (
      <Layout title="Fiche DJ" activeTab="dj">
        <HeaderCard
          title="Candidatez pour un créneau DJ"
          description="Déposez votre demande de passage et précisez le style ainsi que le créneau souhaité."
        />
        <Card>
          <CardContent>
            <Typography variant="h6">Fiche DJ</Typography>
            <Typography>
              Vous devez avoir payé pour accéder à cette page.
            </Typography>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  if (hasRequestedDj) {
    return (
      <Layout title="Fiche DJ" activeTab="dj">
        <HeaderCard
          title="Candidatez pour un créneau DJ"
          description="Déposez votre demande de passage et précisez le style ainsi que le créneau souhaité."
        />
        <Card>
          <CardContent>
            <Typography variant="h6">
              Votre demande a bien été envoyée
            </Typography>
            <Typography>
              Nous vous recontacterons si vous êtes sélectionné. Cette demande
              ne garantit pas que nous pourrons satisfaire votre choix. Il y a
              souvent beaucoup de demandes pour peu de créneaux disponibles,
              mais nous faisons de notre mieux pour satisfaire tout le monde.
            </Typography>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout title="Fiche DJ" activeTab="dj">
      <HeaderCard
        title="Candidatez pour un créneau DJ"
        description="Indiquez le style que vous souhaitez jouer et le créneau qui vous intéresse. L'orga arbitre ensuite selon la programmation."
      />
      <Card sx={{ maxWidth: 640 }}>
        <CardContent>
          <Typography variant="h6">Demande de créneau DJ</Typography>
          <Typography sx={{ color: 'text.secondary', mb: 2 }}>
            Indique le style que tu veux jouer et quand tu veux jouer.
          </Typography>
          <Typography sx={{ color: 'text.primary', mb: 2 }}>
            Important ! Nous ne garantissons pas un créneau à tout le monde.
            Mixer dans notre événement est{' '}
            <strong>un privilège, pas un droit</strong>. Nous faisons de notre
            mieux pour satisfaire tout le monde, mais nous ne pouvons pas le
            garantir. Merci de votre compréhension.
          </Typography>
          <TextField
            margin="normal"
            fullWidth
            label="Style que tu veux jouer"
            placeholder="Ex: techno, hardstyle, commercial, shagass, big room..."
            value={djStyle}
            onChange={(event) => setDjStyle(event.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Quand tu veux jouer"
            placeholder="Ex: vendredi 23h-1h"
            value={djTime}
            onChange={(event) => setDjTime(event.target.value)}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
            {
              "Cette demande ne garantit pas que nous pourrons satisfaire votre choix. La priorité pour nous c'est le style musical."
            }
          </Typography>
          <Typography sx={{ mt: 3 }} variant="subtitle1">
            Règles à respecter
          </Typography>
          <List dense sx={{ pt: 0 }}>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <FiberManualRecordIcon sx={{ fontSize: 8 }} />
              </ListItemIcon>
              <ListItemText primary="Être à l'heure et ne pas dépasser son horaire. S'il y a un décalage, faire valider par l'orga avant." />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <FiberManualRecordIcon sx={{ fontSize: 8 }} />
              </ListItemIcon>
              <ListItemText primary="Pas d'alcool à proximité du matériel" />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <FiberManualRecordIcon sx={{ fontSize: 8 }} />
              </ListItemIcon>
              <ListItemText primary="Limiter le monde derrière les platines" />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <FiberManualRecordIcon sx={{ fontSize: 8 }} />
              </ListItemIcon>
              <ListItemText primary="Ne pas modifier les paramètres comme le volume général" />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <FiberManualRecordIcon sx={{ fontSize: 8 }} />
              </ListItemIcon>
              <ListItemText primary="Le public avant tout, si y'a que toi qui kiff, adapte toi, ou ça dégage" />
            </ListItem>
          </List>
          <FormControlLabel
            control={
              <Checkbox
                checked={hasAcceptedRules}
                onChange={(event) => setHasAcceptedRules(event.target.checked)}
              />
            }
            label="J'accepte les règles ci-dessus"
          />
        </CardContent>
        <CardActions>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={isSubmitDisabled}
          >
            Envoyer la demande
          </Button>
        </CardActions>
      </Card>
    </Layout>
  );
};
